/**
 * Couleurs de maillot — COULEURS-MAILLOT-001.
 *
 * LES ONZE VALEURS SONT UN CONTRAT, PAS UNE PREFERENCE. Le prestataire les
 * attend en minuscules, sans accent, et il est SENSIBLE A LA CASSE : « Noir »
 * vaut un 422, donc une analyse perdue apres un upload et une decoupe deja
 * payes. Ce fichier est le miroir exact de `backend/app/core/kit_colors.py`.
 * Les deux listes doivent rester identiques — un test le verifie.
 *
 * CE QUE LE PRESTATAIRE EN FAIT · UN ECHO. Il renvoie ce qu'on lui donne, dans
 * un bloc `equipes`, sans rien verifier. C'est ce qui ancre `equipe_1` sur
 * notre equipe — et c'est aussi ce qui rend une erreur de saisie invisible :
 * une couleur perimee envoyee ici revient telle quelle, le mapping s'inverse,
 * et le coach lit les statistiques de l'adversaire en croyant lire les siennes.
 * Aucune alerte nulle part.
 *
 * D'ou les deux precautions du lot · l'ecran demande une CONFIRMATION des deux
 * couleurs plutot qu'une saisie qu'on oublie, et le rapport rappelle en clair
 * sur quoi l'analyse a ete faite.
 */

/**
 * `valeur` part au prestataire. `pastille` ne sert QU'A L'AFFICHAGE.
 *
 * Les hex sont volontairement des teintes franches de maillot, pas les couleurs
 * de la charte : ils representent un textile, pas l'identite du produit. Ne pas
 * les brancher sur `theme.js` — un maillot rouge doit rester rouge meme si la
 * charte change, et `T.red` sert deja a dire « mauvaise performance ».
 *
 * `blanc` porte une bordure : sans elle, la pastille disparait sur fond clair.
 */
export const KIT_COLORS = [
  { valeur: 'blanc',  label: 'Blanc',  pastille: '#FFFFFF', bordure: '#D4D4D4' },
  { valeur: 'bleu',   label: 'Bleu',   pastille: '#1D4ED8' },
  { valeur: 'gris',   label: 'Gris',   pastille: '#9CA3AF' },
  { valeur: 'jaune',  label: 'Jaune',  pastille: '#FACC15' },
  { valeur: 'marron', label: 'Marron', pastille: '#78350F' },
  { valeur: 'noir',   label: 'Noir',   pastille: '#111111' },
  { valeur: 'orange', label: 'Orange', pastille: '#F97316' },
  { valeur: 'rose',   label: 'Rose',   pastille: '#EC4899' },
  { valeur: 'rouge',  label: 'Rouge',  pastille: '#DC2626' },
  { valeur: 'vert',   label: 'Vert',   pastille: '#16A34A' },
  { valeur: 'violet', label: 'Violet', pastille: '#7C3AED' },
]

const PAR_VALEUR = new Map(KIT_COLORS.map(c => [c.valeur, c]))

/** Les 11 chaines exactes, dans l'ordre du contrat. */
export const KIT_COLOR_VALUES = KIT_COLORS.map(c => c.valeur)

/**
 * Strict sur la casse, comme le backend et comme le prestataire.
 * Normaliser en douce masquerait une saisie fautive jusqu'a l'analyse.
 */
export function estCouleurValide(v) {
  return typeof v === 'string' && PAR_VALEUR.has(v)
}

/** Libelle affichable. Rend la valeur brute si elle est inconnue — on ne cache
 *  pas une valeur qu'on ne sait pas lire, on la montre telle quelle. */
export function libelleCouleur(v) {
  return PAR_VALEUR.get(v)?.label || v || ''
}

/** Hex de pastille, ou null si la valeur n'est pas au contrat. */
export function pastilleCouleur(v) {
  return PAR_VALEUR.get(v)?.pastille || null
}

export function bordureCouleur(v) {
  return PAR_VALEUR.get(v)?.bordure || 'transparent'
}

/**
 * Les deux couleurs peuvent-elles partir ?
 *
 * Memes trois conditions que le backend · presentes ENSEMBLE, valides, et
 * DIFFERENTES. Le frontend ne fait que devancer le refus pour que le coach le
 * voie a la saisie plutot qu'a l'analyse.
 */
export function couleursEnvoyables(nous, adversaire) {
  return estCouleurValide(nous) && estCouleurValide(adversaire) && nous !== adversaire
}

/**
 * Couleur de NOTRE equipe pour ce match · override du match, sinon le jeu du
 * club correspondant au lieu. Miroir de `resolve_match_kit_colors` cote
 * backend, qui reste l'autorite : ceci ne sert qu'a pre-remplir le formulaire.
 *
 * `is_home` peut valoir null sur une ligne ancienne · traite comme domicile,
 * comme partout ailleurs dans le produit.
 */
export function couleurNousParDefaut(match, club) {
  if (estCouleurValide(match?.kit_color_us)) return match.kit_color_us
  if (!club) return ''
  const domicile = match?.is_home !== false
  const v = domicile ? club.kit_color_home : club.kit_color_away
  return estCouleurValide(v) ? v : ''
}
